import React, { useContext, useState, useEffect, useRef } from 'react';
import PollTab from '../postTabs/PollTab';
import PostTab from '../postTabs/PostTab';
import ClinicalStudyTab from '../postTabs/ClinicalStudyTab';
import { Language } from '../../../App';
import { TypeTab } from './PostForm';
import { IoClose } from 'react-icons/io5';
import { releaseAgendaScrollLock } from '../../../utils/agendaUtils';
import { categorizeFiles, createDropzoneHandlers } from '../../../utils/fileDropUtils';
import '../postTabs/formTheme.css';

function CreatePostPopup({
  selectedLabel,
  refetch,
  selectedPost,
  editWebinar,
  setEditWebinar,
  editPost,
  setEditPost,
  show,
  onHide,
  handleApiCall,
  setProgress,
  handlePostSelect,
  updatePost,
  initialDroppedFiles,
}) {
  const { lang } = useContext(Language);
  const publishId = navigator.language.startsWith("en") ? 'Publish' : "Publicar";
  const clinicalStudyId = lang?.clinical_study || "Clinical Study";
  const pollId = lang?.poll || "Poll";

  const [activeTab, setActiveTab] = useState(
    editPost ? publishId : selectedLabel || publishId
  );
  const [activeDroppedFiles, setActiveDroppedFiles] = useState(initialDroppedFiles || null);
  const [slideIn, setSlideIn] = useState(false);
  const [shake, setShake] = useState(false);
  const shakeTimeoutRef = useRef(null);

  useEffect(() => {
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSlideIn(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    };
  }, []);

  // Sync initialDroppedFiles and auto-route tab if appropriate
  useEffect(() => {
    if (initialDroppedFiles && initialDroppedFiles.length > 0) {
      setActiveDroppedFiles(initialDroppedFiles);
      const { pdfFiles, videoFiles } = categorizeFiles(initialDroppedFiles);
      if (pdfFiles.length > 0 && !editPost) {
        setActiveTab(clinicalStudyId);
      } else if (videoFiles.length > 0 && !editPost) {
        setActiveTab(publishId);
      }
    }
  }, [initialDroppedFiles, editPost, clinicalStudyId, publishId]);

  useEffect(() => {
    if (editPost) {
      setActiveTab(publishId);
    } else if (selectedLabel) {
      // If selectedLabel was Webinar or Clinical Session, default to Publish
      const s = String(selectedLabel).toLowerCase();
      if (s.includes("webinar") || s.includes("session") || s.includes("sesión")) {
        setActiveTab(publishId);
      } else {
        setActiveTab(selectedLabel);
      }
    }
  }, [editPost, selectedLabel, publishId]);

  // Lock background scroll while drawer is open & release on hide/unmount
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          handleModalClose();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        releaseAgendaScrollLock(true);
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      releaseAgendaScrollLock(true);
    }
  }, [show]);

  const handleDrawerDrop = (files) => {
    setActiveDroppedFiles(files);
    const { pdfFiles, videoFiles } = categorizeFiles(files);
    if (pdfFiles.length > 0 && !editPost) {
      setActiveTab(clinicalStudyId);
    } else if (videoFiles.length > 0 && !editPost) {
      setActiveTab(publishId);
    }
  };

  const drawerDropHandlers = createDropzoneHandlers(handleDrawerDrop);

  const commonProps = {
    onHide,
    setProgress,
    handleApiCall,
    refetch,
    initialDroppedFiles: activeDroppedFiles,
  };

  const tabs = [
    {
      id: publishId,
      typeKey: "publish",
      title: lang?.post || lang?.publish || "Post",
      component: (
        <PostTab
          setEditPost={setEditPost}
          editPost={editPost}
          selectedPost={selectedPost}
          handlePostSelect={handlePostSelect}
          updatePost={updatePost}
          {...commonProps}
        />
      ),
    },
    {
      id: pollId,
      typeKey: "poll",
      title: pollId,
      component: <PollTab {...commonProps} />,
    },
    {
      id: clinicalStudyId,
      typeKey: "study",
      title: clinicalStudyId,
      component: <ClinicalStudyTab {...commonProps} />,
    },
  ];

  const activeTypeKey = tabs.find((t) => t.id === activeTab)?.typeKey || "publish";

  const handleModalClose = () => {
    releaseAgendaScrollLock(true);
    setActiveDroppedFiles(null);
    onHide();
    if (setEditWebinar) {
      setEditWebinar(false);
    }
    if (setEditPost) {
      setEditPost(false);
    }
  };

  const isEditMode = Boolean(editPost);

  const handleBackdropClick = () => {
    if (isEditMode) {
      handleModalClose();
      return;
    }
    setShake(true);
    if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    shakeTimeoutRef.current = setTimeout(() => {
      shakeTimeoutRef.current = null;
      setShake(false);
    }, 450);
  };

  if (!show) return null;

  return (
    <div className="pf-drawer-portal">
      {/* Backdrop */}
      <div
        className={`pf-drawer-backdrop ${slideIn ? "slide-in" : ""}`}
        onClick={handleBackdropClick}
        role="presentation"
        aria-hidden="true"
      />

      {/* Right-Side Drawer Panel */}
      <aside
        className={`pf-drawer-panel pf-scope-${activeTypeKey} ${slideIn ? "slide-in" : ""} ${shake ? "shake" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? (lang?.edit_post || "Edit Post") : (lang?.create_post || "Create Post")}
        {...drawerDropHandlers}
      >
        {!isEditMode ? (
          <div className="pf-drawer-header-wrapper">
            <div className="pf-drawer-tabs">
              {tabs.map(({ id, typeKey, title }) => (
                <TypeTab
                  key={id}
                  label={title}
                  typeKey={typeKey}
                  isActive={activeTab === id}
                  onClick={() => {
                    if (activeTab !== id) {
                      setActiveDroppedFiles(null);
                      setActiveTab(id);
                    }
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              className="pf-drawer-close-btn"
              onClick={handleModalClose}
              aria-label="Close drawer"
            >
              <IoClose size={19} />
            </button>
          </div>
        ) : (
          <div className="pf-drawer-header-wrapper d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fs-16 fw-700" style={{ color: "#0F1A2B" }}>
              {lang?.edit_post || "Edit Post"}
            </h5>
            <button
              type="button"
              className="pf-drawer-close-btn"
              onClick={handleModalClose}
              aria-label="Close drawer"
            >
              <IoClose size={19} />
            </button>
          </div>
        )}

        <div className="pf-drawer-body">
          {/* Render the active tab component with smooth transition */}
          <div key={activeTab} className="pf-tab-pane-transition">
            {tabs.find(({ id }) => activeTab === id)?.component || tabs[0].component}
          </div>
        </div>
      </aside>
    </div>
  );
}

export default CreatePostPopup;